/* eslint-disable */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdminPayments from "./AdminPayments";
import { Trash2, Pencil, CreditCard, X, Loader2, ShieldCheck, Gift, Search } from "lucide-react";

export default function Admin() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [tab, setTab] = useState("overview");
  const [planUser, setPlanUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [busy, setBusy] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [offerQuery, setOfferQuery] = useState("");

  const load = () => Promise.all([
    api.get("/admin/stats"),
    api.get("/admin/users"),
    api.get("/admin/offers"),
  ]).then(([s, u, o]) => {
    setStats(s.data); setUsers(u.data); setOffers(o.data);
  }).catch(() => toast.error(t("admin.loadError")));

  useEffect(() => { load(); }, []);

  const deleteUser = async (u) => {
    if (!window.confirm(t("admin.confirmDeleteUser", { email: u.email }))) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(t("admin.accountDeleted"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); } finally { setBusy(""); }
  };

  const deleteOffer = async (o) => {
    if (!window.confirm(t("admin.confirmDeleteOffer", { number: o.offer_number || "", client: o.client_name || "" }))) return;
    setBusy(o.id);
    try {
      await api.delete(`/admin/offers/${o.id}`);
      toast.success(t("admin.offerDeleted"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); } finally { setBusy(""); }
  };

  if (!stats) return <div className="p-10 text-sm text-[#94A3B8]">{t("admin.loading")}</div>;

  const uq = userQuery.trim().toLowerCase();
  const filteredUsers = uq
    ? users.filter(u => `${u.email || ""} ${u.full_name || ""} ${u.plan || ""}`.toLowerCase().includes(uq))
    : users;
  const oq = offerQuery.trim().toLowerCase();
  const filteredOffers = oq
    ? offers.filter(o => `${o.offer_number || ""} ${o.client_name || ""} ${o.project_title || ""}`.toLowerCase().includes(oq))
    : offers;

  const cards = [
    { label: t("admin.stat_users"), value: stats.users, accent: "#E2E8F0" },
    { label: t("admin.stat_offers"), value: stats.offers, accent: "#00BFFF" },
    { label: t("admin.stat_revenue"), value: stats.revenue.toFixed(2), accent: "#22C55E" },
    { label: t("admin.stat_subscribers"), value: (stats.plans.basic || 0) + (stats.plans.pro || 0) + (stats.plans.business || 0) + (stats.plans.firma || 0), accent: "#FBBF24" },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-page">
      <div className="overline">{t("admin.overline")}</div>
      <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{t("admin.title")}</h1>

      <div className="mt-8 border-b border-[#1E293B] flex gap-1 overflow-x-auto">
        {[
          { id: "overview", label: t("admin.tab_overview") },
          { id: "payments", label: t("admin.tab_payments") },
          { id: "users", label: t("admin.tab_users") },
          { id: "offers", label: t("admin.tab_offers") },
        ].map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
            className={`px-4 py-3 text-sm font-semibold uppercase tracking-wider border-b-2 transition-colors ${tab === tb.id ? "border-[#00BFFF] text-[#E2E8F0]" : "border-transparent text-[#94A3B8] hover:text-[#E2E8F0]"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1E293B] border border-[#1E293B]">
          {cards.map(c => (
            <div key={c.label} className="bg-[#0B111D] p-6" data-testid={`stat-${c.label}`}>
              <div className="overline">{c.label}</div>
              <div className="font-display text-4xl font-extrabold mt-2" style={{ color: c.accent }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "payments" && <AdminPayments />}

      {tab === "users" && (
        <div className="mt-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="overline">{t("admin.usersTitle")} ({filteredUsers.length}{uq ? ` ${t("admin.ofFrom")} ${users.length}` : ""})</div>
            <div className="relative w-full sm:w-80">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder={t("admin.searchUsers")}
                className="input-flat pl-9" data-testid="user-search" />
            </div>
          </div>
          <div className="border border-[#1E293B] overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead className="bg-[#111C30] text-white">
                <tr>
                  <th className="text-left p-3 font-display">{t("admin.thEmail")}</th>
                  <th className="text-left p-3 font-display">{t("admin.thName")}</th>
                  <th className="text-left p-3 font-display">{t("admin.thPlan")}</th>
                  <th className="text-left p-3 font-display">{t("admin.thStatus")}</th>
                  <th className="text-right p-3 font-display">{t("admin.thActions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-[#94A3B8]" data-testid="user-empty">{t("admin.userEmpty")}</td></tr>
                ) : filteredUsers.map(u => (
                  <tr key={u.id} className="border-b border-[#1E293B] hover:bg-[#111C30]/40" data-testid={`user-row-${u.id}`}>
                    <td className="p-3">{u.email} {u.is_admin && <span className="ml-2 text-[10px] font-bold bg-[#FBBF24] text-[#03070E] px-1.5 py-0.5 rounded">ADMIN</span>}</td>
                    <td className="p-3 text-[#CBD5E1]">{u.full_name}</td>
                    <td className="p-3 font-mono-plex text-[#00BFFF]">{u.plan}</td>
                    <td className="p-3 text-[#94A3B8]">{u.subscription_status}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setPlanUser(u)} title={t("admin.changePlan")} className="p-2 text-[#94A3B8] hover:text-[#00BFFF] hover:bg-[#1E293B] rounded" data-testid={`plan-${u.id}`}><CreditCard size={16} /></button>
                        <button onClick={() => setEditUser({ ...u, new_password: "" })} title={t("admin.edit")} className="p-2 text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#1E293B] rounded" data-testid={`edit-${u.id}`}><Pencil size={16} /></button>
                        <button onClick={() => deleteUser(u)} disabled={u.is_admin || busy === u.id} title={u.is_admin ? t("admin.cantDeleteAdmin") : t("admin.deleteAccount")} className="p-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#1E293B] rounded disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`delete-user-${u.id}`}>
                          {busy === u.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "offers" && (
        <div className="mt-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="overline">{t("admin.offersTitle")} ({filteredOffers.length}{oq ? ` ${t("admin.ofFrom")} ${offers.length}` : ""})</div>
            <div className="relative w-full sm:w-80">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input value={offerQuery} onChange={(e) => setOfferQuery(e.target.value)} placeholder={t("admin.searchOffers")}
                className="input-flat pl-9" data-testid="offer-search" />
            </div>
          </div>
          <div className="border border-[#1E293B] overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#111C30] text-white">
                <tr>
                  <th className="text-left p-3 font-display">{t("admin.thNumber")}</th>
                  <th className="text-left p-3 font-display">{t("admin.thClient")}</th>
                  <th className="text-left p-3 font-display">{t("admin.thProject")}</th>
                  <th className="text-right p-3 font-display">{t("admin.thTotal")}</th>
                  <th className="text-right p-3 font-display">{t("admin.thActions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-[#94A3B8]" data-testid="offer-empty">{t("admin.offerEmpty")}</td></tr>
                ) : filteredOffers.map(o => (
                  <tr key={o.id} className="border-b border-[#1E293B] hover:bg-[#111C30]/40" data-testid={`offer-row-${o.id}`}>
                    <td className="p-3 font-mono-plex text-xs">{o.offer_number}</td>
                    <td className="p-3">{o.client_name}</td>
                    <td className="p-3 max-w-xs truncate">{o.project_title}</td>
                    <td className="p-3 text-right font-mono-plex">{o.totals?.total?.toFixed(2)} RON</td>
                    <td className="p-3 text-right">
                      <button onClick={() => deleteOffer(o)} disabled={busy === o.id} title={t("admin.deleteOffer")} className="p-2 text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#1E293B] rounded disabled:opacity-30" data-testid={`delete-offer-${o.id}`}>
                        {busy === o.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {planUser && <PlanModal user={planUser} onClose={() => setPlanUser(null)} onSaved={() => { setPlanUser(null); load(); }} />}
      {editUser && <EditModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
    </div>
  );
}

function Modal({ title, icon: Icon, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#1E293B]">
          <div className="flex items-center gap-2 font-display font-bold text-lg">{Icon && <Icon size={18} className="text-[#00BFFF]" />} {title}</div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#E2E8F0]"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function PlanModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const PLAN_OPTIONS = [
    { id: "free", label: t("admin.plan_free") },
    { id: "basic", label: t("admin.plan_basic") },
    { id: "pro", label: t("admin.plan_pro") },
    { id: "business", label: t("admin.plan_business") },
  ];
  const [plan, setPlan] = useState(user.plan === "firma" ? "business" : (user.plan || "free"));
  const [days, setDays] = useState(365);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/plan`, { plan, days: Number(days) || 365 });
      toast.success(plan === "free" ? t("admin.planRevoked") : t("admin.planGranted", { plan }));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); } finally { setBusy(false); }
  };
  return (
    <Modal title={t("admin.grantTitle")} icon={Gift} onClose={onClose}>
      <p className="text-sm text-[#94A3B8] mb-4">{t("admin.grantDesc", { email: user.email })}</p>
      <label className="overline block mb-2">{t("admin.planLabel")}</label>
      <select value={plan} onChange={(e) => setPlan(e.target.value)} className="input-flat" data-testid="plan-select">
        {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      {plan !== "free" && (
        <div className="mt-4">
          <label className="overline block mb-2">{t("admin.accessDays")}</label>
          <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="input-flat" data-testid="plan-days" />
        </div>
      )}
      <button onClick={save} disabled={busy} className="btn-primary w-full mt-6 disabled:opacity-50" data-testid="plan-save">
        {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("admin.savePlan")}
      </button>
    </Modal>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ full_name: user.full_name || "", email: user.email || "", is_admin: !!user.is_admin, new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = { full_name: f.full_name, email: f.email, is_admin: f.is_admin };
      if (f.new_password) payload.new_password = f.new_password;
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success(t("admin.accountUpdated"));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); } finally { setBusy(false); }
  };
  return (
    <Modal title={t("admin.editTitle")} icon={Pencil} onClose={onClose}>
      <label className="overline block mb-2">{t("admin.fullName")}</label>
      <input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className="input-flat" data-testid="edit-name" />
      <label className="overline block mb-2 mt-4">{t("admin.emailLabel")}</label>
      <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-flat" data-testid="edit-email" />
      <label className="overline block mb-2 mt-4">{t("admin.newPassword")}</label>
      <input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="input-flat" placeholder={t("admin.newPasswordPh")} data-testid="edit-password" />
      <label className="flex items-center gap-3 mt-4 cursor-pointer" data-testid="edit-admin-toggle">
        <input type="checkbox" checked={f.is_admin} onChange={(e) => setF({ ...f, is_admin: e.target.checked })} className="w-4 h-4 accent-[#00BFFF]" />
        <span className="text-sm flex items-center gap-1"><ShieldCheck size={15} className="text-[#FBBF24]" /> {t("admin.adminRole")}</span>
      </label>
      <button onClick={save} disabled={busy} className="btn-primary w-full mt-6 disabled:opacity-50" data-testid="edit-save">
        {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("admin.saveChanges")}
      </button>
    </Modal>
  );
}
